import angular from "angular";

import { Arrays, AppStatus, RoleLogic, SitemapNode, UserSession } from "@salaxy/core";

import { SessionService } from "./../SessionService";
import { CurrentRoute, SitemapHelper } from "../../helpers";


/**
 * Helper service to generate navigation controls:
 * Top- and side-menus, paths and controls that show the current title.
 * These controls take the navigation logic from an object (sitemap) and are aware of current node / open page on that sitemap.
 * NOTE: This is just an optional helper to make creating simple demo sites easier.
 * There is no need to use NaviService, NaviController or components in your custom site!
 * You can use something completely different.
 */
export class NaviService {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["$rootScope", "$location", "SessionService", "$injector"];

  /**
   * Contains information about the current sitemap node (if resoved) and other data related to route.
   * This property takes into account that the current node may not be found,
   * i.e. current.node may be null, current.path may be [].
   * However, the "route" as an object should never be null.
   * Most other properties in NaviService use first node as default for historical reasons.
   */
  public current: CurrentRoute = new CurrentRoute(null);

  /**
   * Section where the current node is located.
   * If current node cannot be resolved, this property defaults to first Sitemap node.
   */
  public currentSection: SitemapNode;

  /**
   * Currently selected sitemap node.
   * If current node cannot be resolved, this property defaults to first Sitemap node.
   */
  public currentNode: SitemapNode;

  /**
   * Current path from root to the current node.
   * If current node cannot be resolved, this property defaults to first Sitemap node.
   */
  public currentPath: SitemapNode[];

  /**
   * Title of the section (first level in the sitemap).
   * If current node cannot be resolved, this property defaults to first Sitemap node.
   */
  public sectionTitle: string;

  /**
   * Title of the current sitemap node.
   * If current node cannot be resolved, this property defaults to first Sitemap node.
   */
  public title: string;

  /**
   * True if the navigation (e.g. left sidebar) is open.
   * Typically, this property is used only in narrow (mobile) view, otherwise the sidebar is always shown.
   */
  public isNaviOpen = false;

  /**
   * True if the secondary sidebar (e.g. right sidebar) is open.
   */
  public isSidebarOpen = false;

  /** Sitemap that describes the structure of this application: For all roles. */
  private sitemapForAllRoles: SitemapNode[];

  /** Sitemap filtered for the roles of the current user. */
  private sitemapInRole: SitemapNode[];

  /** Cache for whether the system is using test data. */
  private isTest: boolean;

  constructor(
    $rootScope: angular.IRootScopeService,
    private $location: angular.ILocationService,
    private sessionService: SessionService,
    $injector: angular.auto.IInjectorService,
  ) {
    if ($injector.has("SITEMAP")) {
      this.setSitemap($injector.get("SITEMAP"));
    } else {
      this.setSitemap([]);
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    $rootScope.$on("$routeChangeSuccess", (event, current, previous) => {
      this.resolveAndSetCurrentNode();
    });
    sessionService.subscribe($rootScope, () => {
      this.updateSitemapInRole();
    });
  }

  /**
   * Gets the current sitemap.
   * @returns Sitemap filtered for the roles of the current user.
   */
  public getSitemap(): SitemapNode[] {
    return this.sitemapInRole;
  }

  /**
   * Sets the sitemap any time during the application lifecycle.
   * Alternative way is to set angular constant "SITEMAP", but that must be done before config.
   * @param newSitemap New sitemap to use.
   */
  public setSitemap(newSitemap: SitemapNode[]) {
    this.sitemapForAllRoles = SitemapHelper.setSitemapTitles(newSitemap);
    this.updateSitemapInRole();
  }

  /**
   * Gets the current section: The first level node in the sitemap
   * @returns Section filtered by the roles.
   */
  public getCurrentSection(): SitemapNode {
    return this.currentSection;
  }

  /** Determines if a given node is the current node */
  public isCurrent(siteMapNode: SitemapNode): boolean {
    if (!siteMapNode || !this.currentPath) {
      return false;
    }
    return !!this.currentPath.find((x) => x === siteMapNode);
  }

  /** Determines if a given section is the current section */
  public isCurrentSection(section: SitemapNode): boolean {
    return this.currentSection && section && this.currentSection.url === section.url;
  }

  /**
   * Get sitemap node using path.
   * @param path - Page URL path for the node.
   * @returns - Node if exists, otherwise null.
   */
  public getNodeByPath(path: string): SitemapNode | null {
    const sitemap = this.sitemapInRole;

    for (const section of sitemap) {
      const node = this.findNode(section, path);
      if (node) {
        return node;
      }
    }
    return null;
  }

  /**
   * Get sitemap nodes using path.
   * @param path - Page URL path for the node.
   * @returns - Nodes path if exists, otherwise null.
   */
  public getNodesByPath(path: string): SitemapNode[] | null {
    const targetNode = this.getNodeByPath(path);
    if (!targetNode) {
      return null;
    }

    const sitemap = this.sitemapInRole;
    const targetNodePath = [targetNode];

    /*eslint-disable no-constant-condition */
    main:
    while(true) {
      const child = targetNodePath[0];
      for (const section of sitemap) {
        const parent = this.findParentNode(section, child);
        if (parent) {
          targetNodePath.unshift(parent);
          continue main;
        }
      }
      break;
    }
    return targetNodePath;
  }

  /**
   * Filters the sitemap using the role-restrictions in the sitemap
   * @param session - Optional explicitely given session for filtering the sitemap.
   * @param appStatus - Optional explicitely given appStatus for filtering the sitemap. This must be given with explicitely given session.
   */
  public updateSitemapInRole(session: UserSession = null, appStatus: AppStatus = null): void {
    const newSitemap: SitemapNode[] = angular.copy(this.sitemapForAllRoles);
    this.isTest = session ? RoleLogic.isInRole(session, appStatus, "test") : this.sessionService.isInRole("test");
    if (!this.sessionService.isInSomeRole("pro,admin")) {
      // TODO: PRO should have its own service model (service model by role). Until that is implemented, we disable customizations here.
      SitemapHelper.setCustomProperties(session || this.sessionService.session, newSitemap);
    }
    this.sitemapInRole = this.filterSitemapSectionsByRole(newSitemap, session, appStatus);
    this.resolveAndSetCurrentNode();
  }

  /**
   * Resolve current node from location path
   */
  public resolveAndSetCurrentNode(): void {
    const urlFromPath = "#" + this.$location.path().toLowerCase();
    const currentPath = this.resolveCurrent(urlFromPath, this.sitemapInRole);
    this.current = new CurrentRoute(currentPath);
    // Legacy mapping defaults to first sitemap node
    this.currentPath = currentPath || [this.sitemapInRole[0]];
    this.currentSection = this.currentPath[0];
    this.currentNode = this.currentPath[this.currentPath.length - 1];
    this.sectionTitle = this.currentSection?.title;
    this.title = this.currentNode?.title;
  }

  /**
   * Navigate to given path
   * @param path - Path to navigate.
   * @param checkSession - If true, triggers session refresh
   */
   public navigateTo(path: string, checkSession = false) {
    const check = (): Promise<void> => {
      if (!checkSession) {
        return Promise.resolve();
      } else {
        return this.sessionService.checkSession().then( () => {
          return;
        });
      }
    };
    check().then( () => {
      this.$location.path(path);
    });
  }

  /**
   * Evaluates wildcard string.
   * @param nodeUrl - URL in the sitemap file (e.g. "filename.html#/this/is/nodeUrl"). From this, we take only the hash part ("#/this/is/nodeUrl").
   * @param pathUrl - URL to evaluate - this is the hash path (e.g. "#/this/is/pathUrl")
   * @returns Boolean whether the path url resides within the given node url.
   */
  private checkUrl(nodeUrl: string, pathUrl: string): boolean {
    if (nodeUrl.indexOf("#") !== -1) {
      nodeUrl = nodeUrl.substr(nodeUrl.indexOf("#"));
    }
    // Check and replace route parameters (:-strings) with wildcard *
    const parts: string[] = nodeUrl.split("/");
    // eslint-disable-next-line @typescript-eslint/no-for-in-array
    for (const i in parts) {
      if (parts[i].indexOf(":") === 0) {
        parts[i] = "*";
      }
    }
    const newNodeUrl = parts.join("/");
    return this.wildCardMatch(newNodeUrl, pathUrl);
  }

  /**
   * Evaluates wildcard string
   * @param rule - Wildcard rule
   * @param str - String to evaluate
   * @returns Boolean whether the string matches the given wildcard rule.
   */
  private wildCardMatch(rule: string, str: string): boolean {
    // "."  => Find a single character, except newline or line terminator
    // ".*" => Matches any string that contains zero or more characters
    rule = rule.split("*").join(".*");

    // "^"  => Matches any string with the following at the beginning of it
    // "$"  => Matches any string with that in front at the end of it
    rule = "^" + rule + "$";

    // Create a regular expression object for matching string
    const regex = new RegExp(rule, "i");

    // Returns true if it finds a match, otherwise it returns false
    return regex.test(str);
  }

  /**
   * Recursive resolver for any depth of sitemap nodes.
   * @param url Url to check for.
   * Note that in AngularJS paths, this should be the NG path starting with "#".
   * @param nodes Nodes to check (typically the whole sitemap).
   * @returns The path to current node or null if the current node cannot be resolved.
   */
  private resolveCurrent(url: string, nodes: SitemapNode[] | null): SitemapNode[] | null {
    if (!nodes || nodes.length === 0) {
      return null;
    }
    // home alias for root, this does not have any routeparameters
    for (const node of nodes) {
      const childMatch = this.resolveCurrent(url, node.children);
      if (childMatch) {
        return [node, ...childMatch];
      }
      if (this.checkUrl(node.url, url)) {
        return [node];
      }
    }
    return null;
  }

  /**
   * Filters a node tree bases on user roles.
   * @param nodes - Array of nodes to filter
   * @param session - Optional explicitely given session for filtering the sitemap.
   * @param appStatus - Optional explicitely given appStatus for filtering the sitemap. This must be given with explicitely given session.
   * @returns Filtered list of nodes.
   */
  private filterSitemapSectionsByRole(nodes: SitemapNode[], session: UserSession = null, appStatus: AppStatus = null): SitemapNode[] {
    if (!nodes) {
      return [];
    }

    const isInSomeRole = (roles: string | string[]) => {
      return session ?
        RoleLogic.isInSomeRole(session, appStatus,  Arrays.assureArray(roles)):
        this.sessionService.isInSomeRole(roles);
    };

    const filteredNodes = nodes.filter((node) => {
      const roles = Arrays.assureArray(node.roles);
      if (isInSomeRole(roles)) {
        if (this.isTest && roles.length) {
          // Add marker that this node is visible only because we are in test mode.
          const rolesWithoutTest = roles.filter((x) => x.trim().toLowerCase() !== "test");
          if (rolesWithoutTest.length == 0 ||
             (rolesWithoutTest.length !== roles.length && !isInSomeRole(rolesWithoutTest))
            ) {
              node.visibleTestOnly = true;
          }
        }
        return true;
      } else {
        return false;
      }
    });
    filteredNodes.forEach((node) => {
      node.children = this.filterSitemapSectionsByRole(node.children, session, appStatus);
    });
    return filteredNodes;
  }

  private findNode(parent: SitemapNode, path: string): SitemapNode | null {
    if (parent && path) {
      if (this.checkUrl(parent.url, path)) {
        return parent;
      }
      if (parent.children) {
        for (const child of parent.children) {
          const childNode = this.findNode(child, path);
          if (childNode) {
            return childNode;
          }
        }
      }
    }
    return null;
  }

  private findParentNode(parent: SitemapNode, node: SitemapNode): SitemapNode | null  {
    if (parent.children) {
      for(const child of parent.children) {
        if (child == node) {
          return parent;
        }
        const childParent = this.findParentNode(child, node);
        if (childParent) {
          return childParent;
        }
      }
    }
    return null;
  }

}
